import {
  PortalToFollowElem,
  PortalToFollowElemContent,
  PortalToFollowElemTrigger,
} from '@neo/blocks/portal-to-follow'
import { cn } from '@neo/lib/utils'
import { Tooltip } from '@neo/primitives/tooltip'
import { Handle, Position, useReactFlow } from '@xyflow/react'
import { ChevronsUpDown, Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ZoomScaled } from '../components/zoom-scaled'
import { MenuItems } from '../panel'
import { useFlowStore } from '../store'
import { FlowNodeTypes, type GenericNode, type OutputState } from '../types'
import { isAiAgentOrActionNode } from '../utils/ai-agent-utils'
import { useOutputStates } from '../utils/output-states'
import { PENDING_EDGE_HANDLE } from '../utils/pending-edge'

interface ConnectedHandleProps {
  id: string
  type: 'source' | 'target'
  position: Position
  className?: string
}

const ConnectedHandle = ({ id, type, position, className }: ConnectedHandleProps) => (
  <Handle type={type} position={position} id={id} className={className} isConnectable={false} />
)

interface NodeOutputHandlesProps {
  id: string
  node?: GenericNode
}

interface SectionAddButtonsProps {
  nodeId: string
  node?: GenericNode
  sections: Map<string, OutputState[]>
  unConnectedIds: Set<string>
  isEditing: boolean
}

/** Fixed-outcome nodes whose paths carry section labels (screening) get one
 * labeled attach button per section, each anchoring its section's edges and
 * opening a picker scoped to that section's paths.
 *
 * The picker renders INLINE inside the node (like the drag-out menu node), so
 * it scales and pans with the canvas natively. That placement needs three
 * escapes from react-flow's canvas handling: `nodrag nopan nowheel` on the
 * panel, a z-index raise on the node while open (an inline panel can't paint
 * over neighboring nodes otherwise), and manual outside-press dismissal. */
const SectionAddButtons = ({
  nodeId,
  node,
  sections,
  unConnectedIds,
  isEditing,
}: SectionAddButtonsProps) => {
  const [openSection, setOpenSection] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { updateNode } = useReactFlow()

  const toggle = useCallback(
    (section: string | null) => {
      setOpenSection(section)
      // Every flow node sits at zIndex 1000 (data-transformer stamps it on
      // load), so the open panel needs one more to paint over neighbors.
      updateNode(nodeId, { zIndex: section ? 1001 : 1000 })
    },
    [nodeId, updateNode],
  )

  useEffect(() => {
    if (!openSection) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Element | null
      if (!target) return
      if (containerRef.current?.contains(target)) return
      // The node-select dialog (opened after picking a path) is its own surface.
      if (target.closest('[role="dialog"]')) return
      toggle(null)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [openSection, toggle])

  return (
    <div
      ref={containerRef}
      className={cn(
        'absolute -bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1',
        !isEditing && 'pointer-events-none',
      )}
    >
      {[...sections.entries()].map(([section, states]) => {
        const isOpen = openSection === section
        const unConnected = states.filter((state) => unConnectedIds.has(state.id))
        return (
          <div key={section} className="relative">
            <button
              type="button"
              className={cn(
                // Same type/colors as the edge-chip intent label (edge-label.tsx)
                'relative flex items-center gap-0.5 border rounded-md border-st2 shadow-sm bg-background px-2 py-0.5 font-inter text-s-500 text-primary cursor-pointer z-[100]',
                unConnected.length === 0 && 'border-secondary opacity-50 cursor-not-allowed',
                isOpen && 'border-primary',
              )}
              onClick={(e) => {
                if (unConnected.length === 0) return
                e.stopPropagation()
                toggle(isOpen ? null : section)
              }}
              aria-label={`Add ${section} path`}
              data-testid={`button-add-node-${section.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {states.map((state) => (
                <ConnectedHandle
                  key={state.id}
                  type="source"
                  className="bg-transparent border-0 size-4 pointer-events-none"
                  position={Position.Bottom}
                  id={state.id}
                />
              ))}
              {/* Drag affordance: covers the whole pill (click still bubbles
                  to the button, same trick as the classic "+"). Sectioned
                  pending-handle id so the picker on drop scopes to this
                  section's paths. */}
              {unConnected.length > 0 && (
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id={`${PENDING_EDGE_HANDLE}:${section}`}
                  className="absolute inset-0 size-full transform-none rounded-md border-0 bg-transparent"
                  isConnectable={isEditing}
                />
              )}
              <Plus size={12} />
              {section}
            </button>
            {isOpen && (
              <div className="nodrag nopan nowheel absolute left-0 top-full mt-1.5 z-[110] cursor-default">
                <MenuItems
                  open={isOpen}
                  onOpenChange={(open) => toggle(open ? section : null)}
                  onSelect={() => toggle(null)}
                  opStates={unConnected}
                  pathsTitle={`${section} — Select a path`}
                  currentNodeType={node?.type as FlowNodeTypes}
                  sourceNodeId={nodeId}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export const NodeOutputHandles = ({ id, node }: NodeOutputHandlesProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { unConnectedOpStates, opStates } = useOutputStates(id, node)
  const isAiNode = isAiAgentOrActionNode(node?.type)
  const allowAddNode = unConnectedOpStates.length > 0 || isAiNode
  const isEditing = useFlowStore((state) => state.isEditing)

  // Section buckets in path order; null unless EVERY path carries a section
  // and more than one section exists — only then does the node get per-section
  // attach buttons instead of the single "+".
  const sections = useMemo(() => {
    const buckets = new Map<string, OutputState[]>()
    for (const state of opStates) {
      if (!state.section) return null
      const bucket = buckets.get(state.section) ?? []
      bucket.push(state)
      buckets.set(state.section, bucket)
    }
    return buckets.size > 1 ? buckets : null
  }, [opStates])

  const unConnectedIds = useMemo(
    () => new Set(unConnectedOpStates.map((state) => state.id)),
    [unConnectedOpStates],
  )

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (unConnectedOpStates.length === 0 && !isAiNode) return
      e.stopPropagation()
      setIsOpen((prev) => !prev)
    },
    [unConnectedOpStates.length, isAiNode],
  )
  const showAddNodeIcon = ![FlowNodeTypes.end_conversation, FlowNodeTypes.hangup].includes(
    node?.type as FlowNodeTypes,
  )

  if (sections && !isAiNode && showAddNodeIcon) {
    return (
      <SectionAddButtons
        nodeId={id}
        node={node}
        sections={sections}
        unConnectedIds={unConnectedIds}
        isEditing={isEditing}
      />
    )
  }

  return (
    <div
      className={cn(
        'absolute -bottom-0.5 left-1/2 -translate-x-1/2',
        !isEditing && 'pointer-events-none',
      )}
    >
      <PortalToFollowElem open={isOpen} onOpenChange={setIsOpen} placement="right" offset={20}>
        <PortalToFollowElemTrigger>
          <Tooltip
            content={
              unConnectedOpStates.length > 0
                ? 'Click or drag to add a new node'
                : 'No available paths to connect'
            }
            delayDuration={1000}
            side="bottom"
          >
            {showAddNodeIcon && (
              <button
                type="button"
                className={cn(
                  'flex justify-center items-center border rounded-full border-tertiary absolute -bottom-1.5 left-1/2 -translate-x-1/2 size-4 cursor-pointer z-[100] bg-background',
                  unConnectedOpStates.length === 0 &&
                    !isAiNode &&
                    'border-secondary opacity-50 cursor-not-allowed',
                  isOpen && 'border-primary',
                )}
                onClick={handleClick}
                aria-label="Add node"
                data-testid="button-add-node"
              >
                {opStates.map((state) => (
                  <ConnectedHandle
                    key={state.id}
                    type="source"
                    className="bg-transparent border-0 size-4 pointer-events-none"
                    position={Position.Bottom}
                    id={state.id}
                  />
                ))}
                {allowAddNode && (
                  <Handle
                    type="source"
                    position={Position.Bottom}
                    id="default-edge"
                    className="border-0 size-4 -translate-x-1/2 bg-transparent"
                    isConnectable={isEditing}
                  />
                )}
                {isOpen ? (
                  <ChevronsUpDown size={16} className="text-primary" />
                ) : (
                  <Plus size={16} className="text-primary p-[2px]" />
                )}
              </button>
            )}
          </Tooltip>
        </PortalToFollowElemTrigger>
        <PortalToFollowElemContent>
          <ZoomScaled origin="left center">
            <MenuItems
              open={isOpen}
              onOpenChange={setIsOpen}
              onSelect={() => setIsOpen(false)}
              opStates={unConnectedOpStates}
              currentNodeType={node?.type as FlowNodeTypes}
              sourceNodeId={id}
            />
          </ZoomScaled>
        </PortalToFollowElemContent>
      </PortalToFollowElem>
    </div>
  )
}
